import { Chip, Typography } from '@mui/material'
import { useTranslate } from 'ra-core'
import { createContext, useContext } from 'react'
import { FunctionField, ReferenceField, useGetList, useListContext, useRecordContext } from 'react-admin'

import config from './config'
import AdvancedTextField from '../../components/AdvancedTextField'
import CompactList from '../../components/CompactList'
import DateTooltipField from '../../components/DateTooltipField'
import PeriodField from '../../components/PeriodField'
import PriceField from '../../components/PriceField'
import StaticText from '../../components/StaticText'
import { PAYMENT_ACTION_STATUSES, PAYMENT_ACTIONS } from '../../config/paymentActions'
import { useCommonStyles } from '../../config/theme'
import { getPaymentActionDisplay } from '../../domain/paymentActions'
import { GenericListLayout, ListDatagrid } from '../common/list'
import { useShowStyles } from '../common/show'

const PaymentActionPanel = () => {
  const record = useRecordContext()
  const showClasses = useShowStyles()

  return (
    <ul style={{ paddingLeft: 20 }}>
      <li style={showClasses.unorderedListItem}>
        <StaticText>{'resources.payment-actions.fields.psp_reference'}: </StaticText>
        <AdvancedTextField source="psp_reference" />
      </li>
      <li style={showClasses.unorderedListItem}>
        <StaticText>{'resources.payment-actions.fields.original_psp_reference'}: </StaticText>
        <AdvancedTextField source="original_psp_reference" />
      </li>
      <li style={showClasses.unorderedListItem}>
        <StaticText>{'resources.payment-actions.fields.payloads_request'}: </StaticText>
        {record.payloads?.request ? <pre>{JSON.stringify(record.payloads.request, undefined, 2)}</pre> : 'n/a'}
      </li>
      <li style={showClasses.unorderedListItem}>
        <StaticText>{'resources.payment-actions.fields.payloads_response'}: </StaticText>
        {record.payloads?.response ? <pre>{JSON.stringify(record.payloads.response, undefined, 2)}</pre> : 'n/a'}
      </li>
    </ul>
  )
}

const PaymentActionsUpToCurrentPageContext = createContext([])

const usePaymentActionsUpToCurrentPage = () => {
  const { data = [], page, perPage, sort, resource } = useListContext()
  const userId = data[0]?.user_id
  const { data: paymentActionsUpToCurrentPage = [] } = useGetList(
    resource,
    { pagination: { page: 1, perPage: page * perPage }, sort, filter: { user_id: userId } },
    { enabled: page > 1 && !!userId },
  )
  return page > 1 ? [...paymentActionsUpToCurrentPage, ...data] : data
}

const usePaymentActionDisplay = (record) => {
  const paymentActions = useContext(PaymentActionsUpToCurrentPageContext)
  return getPaymentActionDisplay(record, paymentActions)
}

const PaymentActionStatusChip = ({ record }) => {
  const translate = useTranslate()
  const { status } = usePaymentActionDisplay(record)
  const statusDisplay = PAYMENT_ACTION_STATUSES[status]
  if (!statusDisplay) {
    return null
  }
  return (
    <Chip
      component="span"
      label={translate(statusDisplay.label)}
      size="small"
      color={statusDisplay.color}
      sx={{ borderRadius: 999, fontWeight: 600 }}
    />
  )
}

const PaymentActionRefusalReason = ({ record }) => usePaymentActionDisplay(record).refusalReason || null

const PaymentActionCompactRefusalReason = ({ record }) => {
  const { refusalReason } = usePaymentActionDisplay(record)
  if (!refusalReason) {
    return null
  }
  return (
    <>
      <br />
      <StaticText>{'resources.payment-actions.fields.refusal_reason'}: </StaticText>
      {refusalReason}
    </>
  )
}

const PaymentActionStatusField = (props) => (
  <FunctionField {...props} render={(record) => <PaymentActionStatusChip record={record} />} />
)

const PaymentActionRefusalReasonField = (props) => (
  <FunctionField {...props} render={(record) => <PaymentActionRefusalReason record={record} />} />
)

const PaymentActionsListLayout = (props) => {
  const commonClasses = useCommonStyles()
  const paymentActionsUpToCurrentPage = usePaymentActionsUpToCurrentPage()
  return (
    <PaymentActionsUpToCurrentPageContext.Provider value={paymentActionsUpToCurrentPage}>
      <GenericListLayout
        compactListLayout={
          <CompactList
            icon={<config.icon />}
            title={() => (
              <>
                <Typography component="span" variant="body1" sx={commonClasses.inline} color="textPrimary">
                  <AdvancedTextField source="action" map={PAYMENT_ACTIONS} />
                </Typography>
                <Typography component="span" variant="body1" sx={commonClasses.inline} color="textSecondary">
                  {' • '}
                  <DateTooltipField source="created_on" addTime />
                </Typography>
              </>
            )}
            body={(record) => (
              <>
                {!!record.amount && (
                  <>
                    <StaticText>{'resources.payment-actions.fields.amount'}: </StaticText>
                    <PriceField source="amount" />
                    <br />
                  </>
                )}
                <StaticText>{'resources.payment-actions.fields.status'}: </StaticText>
                <PaymentActionStatusChip record={record} />
                <PaymentActionCompactRefusalReason record={record} />
              </>
            )}
          />
        }
        regularListLayout={
          <ListDatagrid expand={<PaymentActionPanel />}>
            <ReferenceField source="booking_id" reference="bookings" link="show" sortable={false}>
              <PeriodField startedOnSource="start_scheduled_on" endedOnSource="end_scheduled_on" addTime />
            </ReferenceField>
            <DateTooltipField source="created_on" addTime sortable={false} />
            <AdvancedTextField source="action" map={PAYMENT_ACTIONS} sortable={false} />
            <PriceField source="amount" sortable={false} />
            <PaymentActionStatusField source="status" sortable={false} />
            <PaymentActionRefusalReasonField source="refusal_reason" sortable={false} />
          </ListDatagrid>
        }
        {...props}
      />
    </PaymentActionsUpToCurrentPageContext.Provider>
  )
}

export default PaymentActionsListLayout
